import { useEffect, useState } from 'react';
import { institutionApi, offeringsApi } from '../lib/api';

/** Offres de cours : sections, inscriptions, personnel, évaluations, examens. */
export function OfferingsAdminPage() {
  const [institutions, setInstitutions] = useState<any[]>([]);
  const [institution, setInstitution] = useState('');
  const [offerings, setOfferings] = useState<any[]>([]);
  const [terms, setTerms] = useState<any[]>([]);
  const [offering, setOffering] = useState('');
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [staff, setStaff] = useState<any[]>([]);
  const [assessments, setAssessments] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => { institutionApi.institutions().then(setInstitutions).catch(() => setInstitutions([])); }, []);
  useEffect(() => {
    if (!institution) return;
    offeringsApi.offerings(institution).then(setOfferings).catch(() => setOfferings([]));
    offeringsApi.terms(institution).then(setTerms).catch(() => setTerms([]));
  }, [institution]);
  useEffect(() => { if (offering) reloadOffering(); }, [offering]);

  async function reloadOffering() {
    for (const [fn, set] of [
      [() => offeringsApi.enrollments(offering), setEnrollments],
      [() => offeringsApi.staff(offering), setStaff],
      [() => offeringsApi.assessments(offering), setAssessments],
    ] as any[]) {
      try { set(await fn()); } catch { /* vide */ }
    }
  }

  async function run(fn: () => Promise<any>, note: string) {
    setError(''); setNotice('');
    try { await fn(); setNotice(note); if (institution) setOfferings(await offeringsApi.offerings(institution)); await reloadOffering(); }
    catch (e: any) { setError(String(e.message || e)); }
  }

  const ask = (l: string, d = '') => window.prompt(l, d) ?? '';

  return <section className="page">
    <header className="page-header">
      <div>
        <span className="eyebrow">OFFRES</span>
        <h1>Sections, inscriptions et évaluations</h1>
      </div>
    </header>

    {error && <div className="alert error">{error}</div>}
    {notice && <div className="notice">{notice}</div>}

    <div className="card">
      <label>Établissement
        <select value={institution} onChange={(e: any) => { setInstitution(e.target.value); setOffering(''); }}>
          <option value="">—</option>
          {institutions.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
        </select>
      </label>
      {institution && <>
        <button className="button secondary" disabled={!terms.length} onClick={() => {
          const cv = ask('Identifiant de la version de cours'); if (!cv) return;
          const section = ask('Code de section', 'A') || 'A';
          run(() => offeringsApi.createOffering(institution, { term_id: terms[0].id, course_version_id: cv, section_code: section }), 'Offre créée.');
        }}>Créer une offre</button>
        {!terms.length && <p className="technical-note">Aucune période académique : créez-en une depuis la page Établissements.</p>}
        <label>Offre
          <select value={offering} onChange={(e: any) => setOffering(e.target.value)}>
            <option value="">—</option>
            {offerings.map(o => <option key={o.id} value={o.id}>{o.section_code} · {(o.course_version_id || '').slice(0, 8)}</option>)}
          </select>
        </label>
      </>}
    </div>

    {offering && <div className="grid two">
      <div className="card">
        <h3>Inscriptions ({enrollments.length})</h3>
        <button className="button secondary" onClick={() => {
          const s = ask('Identifiant étudiant'); if (!s) return;
          run(() => offeringsApi.enroll(offering, s), 'Étudiant inscrit.');
        }}>Inscrire</button>
        {enrollments.map(e => <div key={e.id} className="order-row">
          <strong>{(e.student_id || '').slice(0, 8)}</strong><span>{e.status}</span>
        </div>)}

        <h3>Personnel ({staff.length})</h3>
        <button className="button secondary" onClick={() => {
          const u = ask('Identifiant utilisateur'); if (!u) return;
          const role = ask('Rôle (INSTRUCTOR, ASSISTANT)', 'INSTRUCTOR') || 'INSTRUCTOR';
          run(() => offeringsApi.addStaff(offering, { user_id: u, role }), 'Personnel affecté.');
        }}>Affecter</button>
        {staff.map(s => <div key={s.id} className="order-row">
          <strong>{(s.user_id || '').slice(0, 8)}</strong><span>{s.role}</span>
        </div>)}
      </div>

      <div className="card">
        <h3>Évaluations ({assessments.length})</h3>
        <button className="button secondary" onClick={() => {
          const title = ask('Intitulé'); if (!title) return;
          run(() => offeringsApi.createAssessment(offering, { title, weight: 1, max_score: 100 }), 'Évaluation créée.');
        }}>Créer</button>
        {assessments.map(a => <div key={a.id} className="order-row">
          <strong>{a.title}</strong>
          <span>coef. {a.weight} · /{a.max_score}</span>
          <button className="button secondary" onClick={() => {
            const enrollment = ask('Identifiant d\'inscription'); if (!enrollment) return;
            const score = Number(ask('Note', '80')); if (Number.isNaN(score)) return;
            run(() => offeringsApi.grade(a.id, { enrollment_id: enrollment, score }), 'Note saisie.');
          }}>Noter</button>
        </div>)}

        <h3>Examen généré par l'IA</h3>
        <button className="button primary" onClick={() => {
          const period = ask('Identifiant de la période d\'examen'); if (!period) return;
          const title = ask('Titre de l\'examen') || 'Examen';
          run(() => offeringsApi.aiGenerateExam(offering, {
            exam_period_id: period, title, exam_type: 'FINAL',
            duration_minutes: 90, max_attempts: 1, weight: 1, question_count: 10,
          }), 'Examen généré.');
        }}>Générer</button>
      </div>
    </div>}
  </section>;
}
